/**
 * Shared types for WorkCards-based bulk download UI.
 * Used by the upsell modal, premium modal, SUTD page, and other bulk download surfaces.
 */

import { WorkId } from 'js/liswp/types';

/**
 * All bucket types used in bulk download filtering.
 * These correspond to the camelCase bucket names returned by the backend.
 */
export const ALL_BUCKET_TYPES = [
  'topRelated',
  'sameAuthor',
  'citingThis',
  'citedByThis',
] as const;

export type BucketType = (typeof ALL_BUCKET_TYPES)[number];

/**
 * Category buckets shown in the filter UI (subset of ALL_BUCKET_TYPES).
 */
export const CATEGORY_BUCKETS: Array<{ key: BucketType; label: string }> = [
  { key: 'citingThis', label: 'Citing this paper' },
  { key: 'citedByThis', label: 'Cited by this paper' },
  { key: 'topRelated', label: 'Similar papers' },
  { key: 'sameAuthor', label: 'By this author' },
];

/**
 * Represents a work in the bulk download bundle.
 */
export interface BundleWork {
  id: number;
  title: string;
  authors: string;
  thumbnailUrl: string;
  viewCount: number;
  /** Bucket/category for this work (camelCased), e.g. "citingThis". */
  bucket?: string;
  /** Index within the bucket (admin-only). */
  bucketIndex?: number;
  /** Similarity score (admin-only). */
  similarityScore?: number;
  /** Publication year. */
  year?: number;
  /** Relevancy percentage (0-100) for display. */
  relevancy?: number;
  /** Abstract or summary of the work. */
  summary?: string;
}

/**
 * Date range filter options.
 */
export type DateRangeFilter =
  | 'all'
  | 'past_year'
  | 'past_5_years'
  | 'past_10_years';

/**
 * Source work information needed by the UI.
 */
export interface SourceWork {
  id: WorkId;
  title: string;
}

/**
 * Props for filter state management - passed to components that need filter control.
 */
export interface FilterState {
  topicQuery: string;
  dateRange: DateRangeFilter;
  selectedBuckets: Set<string>;
}

/**
 * Callbacks for filter changes.
 */
export interface FilterCallbacks {
  onTopicQueryChange: (value: string) => void;
  onDateRangeChange: (value: DateRangeFilter) => void;
  onSelectAllBuckets: () => void;
  onToggleBucket: (bucket: string) => void;
}

/**
 * Shape accepted by PillButton's `trackClick` prop.
 */
export interface PillTrackClick {
  source: string;
  entityId?: number;
  extra?: Record<string, unknown>;
}

/**
 * Factory that builds per-pill trackClick metadata. When provided to
 * FilterPanel, every date-range and category pill emits a click event.
 */
export type BuildPillTrackClick = (pill: {
  filterType: 'date_range' | 'category';
  filterValue: string;
  action?: string;
}) => PillTrackClick;
